// ── 누락 청구 감지 (기공소) — 2026-08-17 대표 확정 ──────────────────────────
// 원칙: "자동 판정기가 아니라 의심 건을 소장에게 보여주는 필터".
//   시스템은 후보(의심)만 제시하고, 청구했는지·빠뜨렸는지의 최종 판단은 사람(소장)이 한다.
//   이 파일은 조회·매칭·추정만 한다 — 발행·정산·금액계산(청구)·commission 로직은 절대 건드리지 않는다.
//
// 입력: 기공소(labId) + 기간(from~to, YYYY-MM-DD)
// 출력: '누락 의심' 후보 목록 (치과별 그룹은 화면에서 처리)
//
// 매칭(케이스가 청구됐나) 2단계:
//   ① item.parsedCaseId 직접조인 우선 (2026-08-17 측정장치 이후 신규 발행분은 정확)
//      + 보조로 orderId 직접조인 (의뢰서→명세서 경로로 청구된 수집 케이스)
//   ② 없으면(과거·수기) 환자명 코어 + 치식 + 치과명 정규화 퍼지 폴백 (신뢰도 낮음 표기)
//
// 누락 의심 = 어느 쪽으로도 매칭 안 된 파싱 케이스. 단 아래는 기계적 제외(명백한 것만):
//   - 리메이크(isRemake)
//   - 미완성 공정(processStage가 밀링 전 = 청구 시점 아님)
//   - in-scope 아님(그 치과로 그 기간에 플랫폼 발행이 한 번도 없으면 = 종이청구 치과)
//   - draft(작성 중 초안)에 이미 있음
//
// 가벼운 dedup: "치과 | 환자 | 치식(union) | 월" 키로 상하악·exocad+3shape 이중을 하나로 묶는다.
//   (무거운 케이스통일 #31 아님 — 오탐 제거용 최소 키만)

import { collection, query, where, getDocs, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { loadCasesForLab } from './caseViewLoad';
import {
    caseToOrderShape, caseItemsOf, teethKeyOf,
    normPatientKey, normClinicKey, monthKeyOf,
} from './caseView';
import {
    buildItemsFromOrders, normalizePriceItems, koreanDisplayName, getTimestamp,
} from './statementItems';
import { partnerKeyOf, isMemberPartnerKey, normPartnerName, getLabBilling, lastClosedPeriod, kstYmd } from './billingCycle';
import { loadLedgerSources } from './receivables';   // 명세서 전량 공용 로더(세션 캐시) — P1-4

// 미완성으로 보는 공정 단계 — 아직 작업 착수 전이라 청구 시점이 명백히 아닌 것만 제외한다.
// 'designing'(설계 중)은 제외하지 않는다: 설계 단계로 남았지만 실제로는 완료·청구 대상인
//   케이스가 조용히 사라지는 것(누락을 놓치는 실패)을 막기 위해, 의심에 올리고 소장이 '작업 중'으로 표시.
// processStage 값이 없으면(수집만 된 케이스 다수) 제외하지 않는다(판단 보류).
const INCOMPLETE_STAGES = new Set(['received', 'scanning']);

// 명세서 항목 toothInfo("#11,12,13 지르코니아")에서 치식 집합만 뽑아 정규화
const teethFromToothInfo = (toothInfo) => {
    const nums = String(toothInfo || '').match(/\d+/g) || [];
    return [...new Set(nums)].sort().join(',');
};

// 케이스 → 안정 dedup 키 (치과 | 환자 | 치식union | 월)
const caseKeyOf = (clinicKey, patientKey, teeth, monthKey) =>
    `${clinicKey}|${patientKey}|${teeth}|${monthKey}`;

// Firestore 문서 id에 안전한 형태 (슬래시·마침표만 치환 — 나머지는 허용)
export const safeCaseKey = (caseKey) =>
    String(caseKey || '').replace(/\//g, '-').replace(/\./g, '_');

// 발행분(청구)으로 인정하는 상태 — draft(작성 중 초안)는 별도 처리(제외 사유)
const isBilledStatus = (s) => s && s !== 'draft';

/**
 * 누락 청구 의심 후보를 계산한다.
 * @param {object} p
 *  - labId : 기공소 id (companyId || uid)
 *  - from, to : 기간 (YYYY-MM-DD, 작업일 fileMtime 기준)
 * @returns {object} {
 *    suspects: [{ caseKey, parsedCaseId, caseIds, clinicId, clinicName, patientName,
 *                 teeth, prosthesisLabel, toothCount, estimate|null, unpriced, confidence('direct'|'fuzzy'), monthKey }],
 *    totalEstimate,     // 추정 합계(단가 아는 건만) — 단정 금지, 추정치
 *    unknownPriceCount, // 단가 미상 건수
 *    scannedCases,      // 훑은 파싱 케이스 수(디버그·설명용)
 *    hasDirectBilling,  // 발행분이 parsedCaseId를 실어 직접조인 가능한가 (신뢰도 안내용)
 *  }
 */
export async function detectMissingClaims({ labId, from, to }) {
    if (!labId) return { suspects: [], totalEstimate: 0, unknownPriceCount: 0, scannedCases: 0, hasDirectBilling: false };

    // 1) 일한 것 — 이 기공소·기간의 파싱 케이스 (excluded/scan_only는 loadCasesForLab가 이미 제외)
    const cases = await loadCasesForLab({ labId, from, to });

    // 2) 청구한 것 — 이 기공소가 발행자인 거래명세서 (조회만)
    const stSnap = await getDocs(query(collection(db, 'transactionStatements'), where('fromUserId', '==', labId)));
    const statements = stSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    // 발행분 / draft 로 분리하며 매칭 인덱스 구성
    const billedCaseIds = new Set();   // 직접조인: 발행분 item.parsedCaseId
    const billedOrderIds = new Set();  // 직접조인 보조: 발행분 item.orderId (의뢰서→명세서 경로)
    const billedFuzzy = new Set();     // 퍼지: clinicKey|patientKey|teeth
    const draftCaseIds = new Set();
    const draftOrderIds = new Set();
    const draftFuzzy = new Set();
    const billedClinicMonths = new Set();  // in-scope 판정: clinicKey|월 (발행분 실제 월 ±1개월)

    let hasDirectBilling = false;
    for (const s of statements) {
        const clinicNameKey = normClinicKey(s.toUserName || '');
        const clinicKey = s.toUserId ? `id:${s.toUserId}` : clinicNameKey;
        const items = Array.isArray(s.items) ? s.items : [];
        const toCaseSet = isBilledStatus(s.status) ? billedCaseIds : (s.status === 'draft' ? draftCaseIds : null);
        const toOrderSet = isBilledStatus(s.status) ? billedOrderIds : (s.status === 'draft' ? draftOrderIds : null);
        const toFuzzySet = isBilledStatus(s.status) ? billedFuzzy : (s.status === 'draft' ? draftFuzzy : null);
        if (!toCaseSet) continue; // 알 수 없는 상태는 매칭 근거로 쓰지 않음

        for (const it of items) {
            if (it && it.parsedCaseId) { toCaseSet.add(it.parsedCaseId); if (isBilledStatus(s.status)) hasDirectBilling = true; }
            if (it && it.orderId) toOrderSet.add(it.orderId);
            // 환자명: 항목에 없으면 명세서 문서레벨 patientName으로 폴백(이미 청구건 오탐 방지)
            const pk = normPatientKey((it && it.patientName) || s.patientName);
            const tk = teethFromToothInfo(it && it.toothInfo);
            if (pk) toFuzzySet.add(`${clinicKey}|${pk}|${tk}`);
        }

        // in-scope: 발행분이면 이 치과가 플랫폼으로 청구된 것 — 발행분의 '실제 월'만 기록한다.
        //   (과거 임의 시점 발행 1건이 조회기간 전체를 in-scope로 넓히던 버그 수정 — 한빛 5월 발행이 7월을 열던 문제)
        if (isBilledStatus(s.status)) {
            // id키·이름키 양쪽으로 기록(파싱 케이스가 partnerId 없이 이름만 있어도 in-scope 인정되게)
            const addScope = (d, off) => {
                const m = new Date(d.getFullYear(), d.getMonth() + off, 1);
                const mk = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`;
                billedClinicMonths.add(`${clinicKey}|${mk}`);
                if (clinicNameKey) billedClinicMonths.add(`${clinicNameKey}|${mk}`);
            };
            // 발행일(publishedAt/createdAt)과 청구기간말(endDate)을 근거 월로 삼고, 발행-작업월 시차는 ±1개월만 흡수
            const bases = [getTimestamp(s.publishedAt) || getTimestamp(s.createdAt), getTimestamp(s.endDate)]
                .filter(Boolean).map(t => new Date(t));
            for (const d of bases) for (let off = -1; off <= 1; off++) addScope(d, off);
        }
    }

    // 3) 단가표 (치과명 정규화 키 → 정규화 항목)
    const plSnap = await getDocs(query(collection(db, 'priceLists'), where('userId', '==', labId)));
    const priceByClinic = new Map();  // normClinicKey → items[]
    plSnap.docs.forEach(d => {
        const x = d.data();
        if ((x.items || []).length) {
            const key = normClinicKey(x.partnerName || String(d.id).split('_').slice(1).join('_') || '');
            priceByClinic.set(key, normalizePriceItems(x.items));
        }
    });

    // 4) 케이스별 의심 판정
    const byKey = new Map();  // caseKey → suspect(dedup)
    for (const r of cases) {
        // 제외 ①: 리메이크
        if (r.isRemake) continue;
        // 제외 ②: 미완성 공정 (밀링 전)
        if (r.processStage && INCOMPLETE_STAGES.has(String(r.processStage))) continue;

        const clinicName = r.partnerName || r.clientNameInFile || '미상';
        const clinicKeyName = normClinicKey(clinicName);
        const clinicKey = r.partnerId ? `id:${r.partnerId}` : clinicKeyName;
        const orderShape = caseToOrderShape(r, { partnerName: clinicName, labId });
        const patientName = orderShape.patientName;
        const patientKey = normPatientKey(patientName);
        const teeth = teethKeyOf(orderShape);
        const monthKey = monthKeyOf(orderShape) || '';

        // 매칭 — 직접조인(parsedCaseId·orderId) 우선
        const directHit = billedCaseIds.has(r.id) || draftCaseIds.has(r.id) ||
            (r.orderId && (billedOrderIds.has(r.orderId) || draftOrderIds.has(r.orderId)));
        if (directHit) continue; // 청구됨(또는 draft에 있음) — 의심 아님

        // 퍼지 폴백 — 정규화 clinic|환자|치식 일치
        const fuzzyKey = `${clinicKey}|${patientKey}|${teeth}`;
        const fuzzyKeyName = `${clinicKeyName}|${patientKey}|${teeth}`;
        const fuzzyHit = patientKey && (
            billedFuzzy.has(fuzzyKey) || draftFuzzy.has(fuzzyKey) ||
            billedFuzzy.has(fuzzyKeyName) || draftFuzzy.has(fuzzyKeyName)
        );
        if (fuzzyHit) continue; // 이름·치식으로 청구 흔적 있음 — 의심에서 뺀다(오탐 방지)

        // 제외 ③: in-scope 아님 — 이 치과로 이 기간에 플랫폼 발행이 전무하면 종이청구 치과로 보고 제외
        const inScope = billedClinicMonths.has(`${clinicKey}|${monthKey}`) ||
            billedClinicMonths.has(`${clinicKeyName}|${monthKey}`);
        if (!inScope) continue;

        // 추정 금액 — 발행 로직과 동일한 buildItemsFromOrders (치아수×단가). 단가 없으면 미상.
        const priceList = priceByClinic.get(clinicKeyName) || [];
        const built = buildItemsFromOrders([orderShape], { priceList });
        let estimate = 0, unpriced = false, toothCount = 0;
        built.forEach(it => {
            const cnt = Number(it.toothCount) || 0;
            toothCount += cnt;
            if (Number(it.unitPrice)) estimate += cnt * Number(it.unitPrice);
            else unpriced = true;
        });
        const hasPrice = priceList.length > 0;
        // 보철 요약 라벨 (한글 표준명 — 표시 전용)
        const prosLabels = [...new Set(caseItemsOf(r).map(i => koreanDisplayName(i.prosthesis, i.material)).filter(Boolean))];
        const prosthesisLabel = prosLabels.join(', ') || (r.indications || [])[0] || '';

        // 신뢰도(케이스별) — 이 케이스가 orderId(웹 의뢰서·수집 명세서화)로 ID 대조가 가능하면 '직접',
        //   ID 연결이 없어 이름·치식으로만 대조 가능한 수집 케이스면 '퍼지'(낮음).
        //   suspect는 정의상 직접조인에 실패한 건이므로, 랩 전체 플래그가 아니라 케이스 단위로 판정한다.
        const confidence = r.orderId ? 'direct' : 'fuzzy';

        const caseKey = caseKeyOf(clinicKey, patientKey, teeth, monthKey);
        const existing = byKey.get(caseKey);
        if (existing) {
            // 가벼운 dedup — 상하악·이중소스: 치식 union, 항목 많은 쪽 대표, 추정은 큰 쪽 유지
            existing.caseIds.push(r.id);
            const mergedTeeth = [...new Set([...String(existing.teeth).split(',').filter(Boolean), ...String(teeth).split(',').filter(Boolean)])]
                .map(n => parseInt(n)).filter(n => !isNaN(n)).sort((a, b) => a - b).join(',');
            existing.teeth = mergedTeeth;
            if ((estimate || 0) > (existing.estimate || 0)) existing.estimate = estimate;
            existing.unpriced = existing.unpriced || unpriced;
            existing.toothCount = Math.max(existing.toothCount, toothCount);
            if (prosthesisLabel && !existing.prosthesisLabel) existing.prosthesisLabel = prosthesisLabel;
            continue;
        }
        byKey.set(caseKey, {
            caseKey,
            parsedCaseId: r.id,           // 대표 케이스 id
            caseIds: [r.id],
            clinicId: r.partnerId || null,
            clinicName,
            patientName,
            teeth,
            prosthesisLabel,
            toothCount,
            estimate: hasPrice ? estimate : null,
            unpriced: hasPrice ? unpriced : true,
            confidence,
            monthKey,
        });
    }

    const suspects = [...byKey.values()].sort((a, b) => {
        // 치과명 → 환자명 순
        const c = a.clinicName.localeCompare(b.clinicName, 'ko');
        return c !== 0 ? c : String(a.patientName).localeCompare(String(b.patientName), 'ko');
    });
    const totalEstimate = suspects.reduce((s, x) => s + (Number(x.estimate) || 0), 0);
    const unknownPriceCount = suspects.filter(x => x.estimate == null || x.unpriced).length;

    return { suspects, totalEstimate, unknownPriceCount, scannedCases: cases.length, hasDirectBilling };
}

// 소장 확인(disposition) 선택지 — 화면 버튼과 저장값의 정본
export const DISPOSITIONS = [
    { key: 'billed', label: '이미 청구됨', color: '#059669' },
    { key: 'paper', label: '종이/외부로 받음', color: '#0EA5E9' },
    { key: 'in_progress', label: '아직 작업 중', color: '#B45309' },
    { key: 'missed', label: '진짜 빠뜨림', color: '#DC2626' },
    { key: 'na', label: '명세서 대상 아님', color: '#64748B' },
];
export const DISPOSITION_LABEL = DISPOSITIONS.reduce((m, d) => { m[d.key] = d.label; return m; }, {});

// ════════════════════════════════════════════════════════════════════════════
// 청구 안 된 작업 요약 (2026-10-23 거래명세서 1단계 — 명세 §2-2, 계약 §5)
// 기존 detectMissingClaims 와 다른 점:
//   - '청구됨' = 살아 있는 발행본(confirmed/published, deletedBySender != true)에만 있을 때.
//     지워진 초안·삭제 표시된 발행본에만 있던 건은 '청구 안 됨'으로 잡는다.
//   - 초안에만 있는 건은 따로 센다(draftOnlyCount). 리메이크는 금액 없이 따로 센다(remakeCount).
//   - 기간 고정: 최근 3개월 ~ 지난달 말(그 치과에 청구 마감일이 있으면 가장 최근 지난 마감일까지).
// 환자명은 rows(대표 PIN 화면 펼침 표 전용)에만 담긴다 — 로그·새 문서에는 넣지 않는다.
// ════════════════════════════════════════════════════════════════════════════

const stripCasePrefix = (id) => String(id || '').replace(/^case:/, '');
const isLiveIssued = (s) => ['confirmed', 'published'].includes(s.status) && s.deletedBySender !== true;

// 명세서 → 대조 인덱스 (live = 살아 있는 발행본, draft = 작성 중). 삭제 표시본·기타 상태는 대조 근거에서 뺀다.
function buildStatementIndex(statements) {
    const mk = () => ({ caseIds: new Set(), orderIds: new Set(), fuzzy: new Set() });
    const live = mk(), draft = mk();
    const scopeMonths = new Set();   // in-scope: 이 치과로 플랫폼 명세서(삭제 표시본 포함, 초안 제외)를 낸 달 ±1
    for (const s of statements) {
        const clinicNameKey = normClinicKey(s.toUserName || '');
        const clinicKey = s.toUserId ? `id:${s.toUserId}` : clinicNameKey;
        const target = isLiveIssued(s) ? live : (s.status === 'draft' ? draft : null);
        if (target) {
            for (const it of (Array.isArray(s.items) ? s.items : [])) {
                if (!it) continue;
                if (it.parsedCaseId) target.caseIds.add(stripCasePrefix(it.parsedCaseId));
                if (it.caseId) target.caseIds.add(stripCasePrefix(it.caseId));
                if (it.orderId) {
                    target.orderIds.add(it.orderId);
                    if (String(it.orderId).startsWith('case:')) target.caseIds.add(stripCasePrefix(it.orderId));
                }
                const pk = normPatientKey(it.patientName || s.patientName);
                const tk = teethFromToothInfo(it.toothInfo);
                if (pk) target.fuzzy.add(`${clinicKey}|${pk}|${tk}`);
            }
        }
        if (s.status && s.status !== 'draft') {
            const bases = [getTimestamp(s.publishedAt) || getTimestamp(s.createdAt), getTimestamp(s.endDate)]
                .filter(Boolean).map(t => new Date(t));
            for (const d of bases) for (let off = -1; off <= 1; off++) {
                const m = new Date(d.getFullYear(), d.getMonth() + off, 1);
                const mk2 = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`;
                scopeMonths.add(`${clinicKey}|${mk2}`);
                if (clinicNameKey) scopeMonths.add(`${clinicNameKey}|${mk2}`);
            }
        }
    }
    return { live, draft, scopeMonths };
}

const hitIn = (ix, r, fuzzyKeys) =>
    ix.caseIds.has(r.id) || (r.orderId && ix.orderIds.has(r.orderId)) || fuzzyKeys.some(k => k && ix.fuzzy.has(k));

// 처분 기록 인덱스 — 새 id({labId}_{parsedCaseId}, caseIds 필드)와 옛 id(caseKey 필드) 모두 인정
export function buildReviewIndex(reviewDocs) {
    const byCaseId = new Map();   // parsedCaseId → { disposition, docId, legacy:false }
    const byCaseKey = new Map();  // 옛 caseKey → { disposition, docId, legacy:true }
    for (const d of reviewDocs) {
        const x = d.data || {};
        if (!x.disposition) continue;
        if (x.caseKey) {
            // 옛 형식(id·필드에 환자명) — caseKey 와 parsedCaseId 둘 다로 인정. 새 형식이 있으면 새 형식이 우선
            const v = { disposition: x.disposition, docId: d.id, legacy: true };
            byCaseKey.set(x.caseKey, v);
            if (x.parsedCaseId && !byCaseId.has(x.parsedCaseId)) byCaseId.set(x.parsedCaseId, v);
            continue;
        }
        const ids = new Set([...(Array.isArray(x.caseIds) ? x.caseIds : []), x.parsedCaseId].filter(Boolean));
        ids.forEach(id => {
            const cur = byCaseId.get(id);
            if (!cur || cur.legacy) byCaseId.set(id, { disposition: x.disposition, docId: d.id, legacy: false });
        });
    }
    return { byCaseId, byCaseKey };
}

export const reviewOfRow = (rix, row) => {
    for (const id of row.caseIds) { const v = rix.byCaseId.get(id); if (v) return v; }
    return rix.byCaseKey.get(row.caseKey) || null;
};

const kstMonthStartBefore = (now, monthsBack) => {
    const [y, m] = kstYmd(now).split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1 - monthsBack, 1));
    return d.toISOString().slice(0, 10);
};
const kstLastMonthEnd = (now) => {
    const [y, m] = kstYmd(now).split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, 0)).toISOString().slice(0, 10);
};

/**
 * 순수 판정 (단위 시험용) — 케이스·명세서·단가표·처분·청구 마감일로 요약을 만든다.
 * @param p { cases, statements, priceDocs:[{id, data}], reviewDocs:[{id, data}], billingPartners, labId, now }
 */
export function classifyUnbilled({ cases = [], statements = [], priceDocs = [], reviewDocs = [], billingPartners = {}, labId = '', now = new Date() }) {
    const ix = buildStatementIndex(statements);
    const rix = buildReviewIndex(reviewDocs);
    const priceByClinic = new Map();
    priceDocs.forEach(d => {
        const x = d.data || {};
        if ((x.items || []).length) {
            const key = normClinicKey(x.partnerName || String(d.id).split('_').slice(1).join('_') || '');
            priceByClinic.set(key, normalizePriceItems(x.items));
        }
    });
    // 이름 → 회원 uid (partnerId 없이 이름만 있는 수집 케이스를 회원 치과 키로 — 명세서·단가표의 회원 거래처 기준).
    //   같은 정규화 이름에 uid 가 둘 이상이면 애매하므로 이름 키 유지
    const memberUidsByName = new Map();
    const addMember = (uid, name) => {
        const n = normPartnerName(name);
        if (!n || !uid || !isMemberPartnerKey(partnerKeyOf(uid, name))) return;
        if (!memberUidsByName.has(n)) memberUidsByName.set(n, new Set());
        memberUidsByName.get(n).add(uid);
    };
    statements.forEach(st => addMember(st.toUserId, st.toUserName));
    priceDocs.forEach(d => addMember(d.data?.partnerId, d.data?.partnerName));
    const casePartnerKey = (r, clinicName) => {
        if (r.partnerId) return partnerKeyOf(r.partnerId, clinicName);
        const set = memberUidsByName.get(normPartnerName(clinicName));
        if (set && set.size === 1) return [...set][0];
        return partnerKeyOf('', clinicName);
    };
    const stmtById = new Map(statements.map(st => [st.id, st]));
    const defaultCutoff = kstLastMonthEnd(now);
    const cutoffCache = new Map();
    const cutoffOf = (pkey) => {
        if (cutoffCache.has(pkey)) return cutoffCache.get(pkey);
        const cd = billingPartners?.[pkey]?.closingDay;
        const p = cd ? lastClosedPeriod(cd, now) : null;
        const c = p ? p.to : defaultCutoff;
        cutoffCache.set(pkey, c);
        return c;
    };

    const byKey = new Map();
    const neverBilledClinics = new Set();
    for (const r of cases) {
        if (r.sourceType === 'scan_only') continue;
        if (!['pending', 'imported'].includes(r.status)) continue;
        if (r.processStage && INCOMPLETE_STAGES.has(String(r.processStage))) continue;

        const clinicName = r.partnerName || r.clientNameInFile || '미상';
        const clinicKeyName = normClinicKey(clinicName);
        const clinicKey = r.partnerId ? `id:${r.partnerId}` : clinicKeyName;
        const pkey = casePartnerKey(r, clinicName);
        const workDate = String(r.fileMtime || '').slice(0, 10);
        if (!workDate || workDate > cutoffOf(pkey)) continue;   // 아직 청구 시점 아님 (이번 달·마감 전)

        const orderShape = caseToOrderShape(r, { partnerName: clinicName, labId });
        const patientName = orderShape.patientName;
        const patientKey = normPatientKey(patientName);
        const teeth = teethKeyOf(orderShape);
        const monthKey = monthKeyOf(orderShape) || '';
        const fuzzyKeys = patientKey ? [`${clinicKey}|${patientKey}|${teeth}`, `${clinicKeyName}|${patientKey}|${teeth}`] : [];

        if (hitIn(ix.live, r, fuzzyKeys)) continue;   // 청구됨
        const draftOnly = hitIn(ix.draft, r, fuzzyKeys);
        const kind = r.isRemake ? 'remake' : (draftOnly ? 'draft_only' : 'unbilled');

        // in-scope: 플랫폼으로 그 달(±1) 명세서를 낸 적 있는 치과, 또는 이 케이스가 한 번이라도 명세서에 담겼던(statementId) 건
        const inScope = ix.scopeMonths.has(`${clinicKey}|${monthKey}`) || ix.scopeMonths.has(`${clinicKeyName}|${monthKey}`) ||
            !!r.statementId || draftOnly;
        if (!inScope) { neverBilledClinics.add(clinicKey); continue; }

        const priceList = priceByClinic.get(clinicKeyName) || [];
        let estimate = 0, unpriced = false, toothCount = 0;
        if (kind !== 'remake') {
            buildItemsFromOrders([orderShape], { priceList }).forEach(it => {
                const cnt = Number(it.toothCount) || 0;
                toothCount += cnt;
                if (Number(it.unitPrice)) estimate += cnt * Number(it.unitPrice);
                else unpriced = true;
            });
        }
        const hasPrice = priceList.length > 0;
        const prosLabels = [...new Set(caseItemsOf(r).map(i => koreanDisplayName(i.prosthesis, i.material)).filter(Boolean))];
        const prosthesisLabel = prosLabels.join(', ') || (r.indications || [])[0] || '';

        const caseKey = `${kind}|${caseKeyOf(clinicKey, patientKey, teeth, monthKey)}`;
        const existing = byKey.get(caseKey);
        if (existing) {
            existing.caseIds.push(r.id);
            existing.teeth = [...new Set([...String(existing.teeth).split(',').filter(Boolean), ...String(teeth).split(',').filter(Boolean)])]
                .map(n => parseInt(n, 10)).filter(n => !isNaN(n)).sort((a, b) => a - b).join(',');
            if ((estimate || 0) > (existing.estimate || 0)) existing.estimate = estimate;
            existing.unpriced = existing.unpriced || unpriced;
            existing.toothCount = Math.max(existing.toothCount, toothCount);
            if (prosthesisLabel && !existing.prosthesisLabel) existing.prosthesisLabel = prosthesisLabel;
            if (workDate < existing.workDate) existing.workDate = workDate;
            continue;
        }
        byKey.set(caseKey, {
            kind,                       // 'unbilled' | 'draft_only' | 'remake'
            caseKey,                    // 메모리 전용(환자명 포함) — 저장 금지
            legacyCaseKey: caseKeyOf(clinicKey, patientKey, teeth, monthKey),   // 옛 처분 문서 대조용
            parsedCaseId: r.id,
            caseIds: [r.id],
            clinicId: r.partnerId || null,
            clinicName,
            partnerKey: pkey,
            patientName,
            teeth,
            prosthesisLabel,
            toothCount,
            estimate: kind === 'remake' ? null : (hasPrice ? estimate : null),
            unpriced: kind === 'remake' ? false : (hasPrice ? unpriced : true),
            confidence: r.orderId ? 'direct' : 'fuzzy',
            monthKey,
            workDate,
            // 지워진 명세서에 묶였던 흔적 — statementId 가 가리키는 명세서가 없거나 삭제 표시일 때만
            wasInStatement: !!r.statementId && (!stmtById.has(r.statementId) || stmtById.get(r.statementId).deletedBySender === true),
        });
    }

    const rows = [...byKey.values()].map(row => {
        const rv = reviewOfRow(rix, { caseIds: row.caseIds, caseKey: row.legacyCaseKey });
        // 대표 케이스 id 는 처분 문서가 가리키는 쪽으로 고정(조회 순서가 바뀌어도 같은 문서에 저장)
        return { ...row, review: rv ? { disposition: rv.disposition, docId: rv.docId, legacy: rv.legacy } : null };
    }).sort((a, b) => {
        const c = a.clinicName.localeCompare(b.clinicName, 'ko');
        return c !== 0 ? c : String(a.patientName).localeCompare(String(b.patientName), 'ko');
    });

    const open = rows.filter(x => !x.review);
    const unbilledOpen = open.filter(x => x.kind === 'unbilled');
    return {
        count: unbilledOpen.length,
        amountEstimate: unbilledOpen.reduce((s, x) => s + (Number(x.estimate) || 0), 0),
        unknownPriceCount: unbilledOpen.filter(x => x.estimate == null || x.unpriced).length,
        draftOnlyCount: open.filter(x => x.kind === 'draft_only').length,
        remakeCount: open.filter(x => x.kind === 'remake').length,
        neverBilledClinicCount: neverBilledClinics.size,
        rows,
        reviewedCount: rows.filter(x => !!x.review).length,
        error: null,
    };
}

/**
 * 청구 안 된 작업 요약 (거래명세서 목록 카드 ①).
 * @returns {Promise<{ count, amountEstimate, unknownPriceCount, draftOnlyCount, remakeCount, neverBilledClinicCount, rows, reviewedCount, error }>}
 *   실패 시 throw 하지 않고 error 에 코드/메시지.
 */
export async function computeUnbilledSummary(labId, { now = new Date(), force = false } = {}) {
    const empty = { count: 0, amountEstimate: 0, unknownPriceCount: 0, draftOnlyCount: 0, remakeCount: 0, neverBilledClinicCount: 0, rows: [], reviewedCount: 0 };
    if (!labId) return { ...empty, error: 'labId 없음' };
    try {
        const from = kstMonthStartBefore(now, 3);
        const to = kstYmd(now);
        // 명세서는 받을 돈 원장과 같은 공용 로더(세션 캐시)로 — 목록 열 때 전량을 두 번 읽지 않는다 (2026-10-24 재점검 P1-4).
        //   발행·삭제 뒤에는 호출부가 invalidateReceivables(labId) 를 부르므로 같은 시점의 자료를 본다.
        const [cases, ledger, plSnap, rvSnap] = await Promise.all([
            loadCasesForLab({ labId, from, to }),
            loadLedgerSources(labId, { force }),   // force = 카드 [다시 계산] — 캐시를 버리고 새로 읽는다
            getDocs(query(collection(db, 'priceLists'), where('userId', '==', labId))),
            getDocs(query(collection(db, 'missingClaimReviews'), where('labId', '==', labId))),
        ]);
        let billingPartners = {};
        try {
            billingPartners = (await getLabBilling(labId)).partners;
        } catch (e) {
            // 청구 마감일을 못 읽으면 '지난달 말' 기준으로 계산을 이어간다(숫자는 나오되 기록은 남김)
            console.warn('[missingClaims] 청구 마감일 읽기 실패 — 지난달 말 기준으로 계산', e?.code || e?.message);
        }
        const res = classifyUnbilled({
            cases,
            statements: ledger.statements,
            priceDocs: plSnap.docs.map(d => ({ id: d.id, data: d.data() })),
            reviewDocs: rvSnap.docs.map(d => ({ id: d.id, data: d.data() })),
            billingPartners, labId, now,
        });
        console.log('[missingClaims] 요약', 'cases', cases.length, 'unbilled', res.count, 'draftOnly', res.draftOnlyCount, 'remake', res.remakeCount);
        return res;
    } catch (e) {
        console.error('[missingClaims] 요약 계산 실패', e?.code || e?.message || e);
        return { ...empty, error: e?.code || e?.message || '계산 실패' };
    }
}

/** 새 처분 문서 id — 환자명 없음 */
export const reviewDocIdOf = (labId, parsedCaseId) => `${labId}_${parsedCaseId}`;

/**
 * 소장 처분 저장 — missingClaimReviews/{labId}_{parsedCaseId}, patientName 없음 (rules 허용 키 8개만).
 *   이미 새 형식 문서로 처분된 행이면 그 문서를 고쳐 쓴다. 옛 형식 문서(환자명 포함)는 새 문서를 쓴 뒤 지운다.
 * @returns {Promise<{ disposition, docId }>}
 */
export async function saveReview(labId, row, disposition) {
    if (!labId || !row || !row.parsedCaseId) throw new Error('labId·parsedCaseId 없음');
    if (!DISPOSITIONS.some(d => d.key === disposition)) throw new Error('알 수 없는 처분');
    const docId = (row.review && !row.review.legacy && row.review.docId) || reviewDocIdOf(labId, row.parsedCaseId);
    const parsedCaseId = docId.slice(labId.length + 1);
    await setDoc(doc(db, 'missingClaimReviews', docId), {
        labId,
        parsedCaseId,
        caseIds: Array.isArray(row.caseIds) && row.caseIds.length ? row.caseIds.slice(0, 50) : [parsedCaseId],
        clinicName: String(row.clinicName || '').slice(0, 100),
        teeth: String(row.teeth || ''),
        monthKey: String(row.monthKey || ''),
        disposition,
        updatedAt: serverTimestamp(),
    });
    if (row.review && row.review.legacy && row.review.docId) {
        try {
            await deleteDoc(doc(db, 'missingClaimReviews', row.review.docId));
        } catch (e) {
            // 새 문서는 저장됨 — 옛 문서는 일괄 정리 스크립트가 지운다
            console.warn('[missingClaims] 옛 처분 문서 삭제 실패(옛 id는 환자명 포함이라 기록 안 함)', row.parsedCaseId, e?.code || e?.message);
        }
    }
    console.log('[missingClaims] 처분 저장', docId);
    return { disposition, docId };
}

/** 처분 되돌리기 — 그 행에 붙은 처분 문서(새·옛 형식)를 지운다. 없는 문서 삭제는 rules가 거부하므로 있는 것만 */
export async function deleteReview(labId, row) {
    if (!labId || !row) throw new Error('labId·row 없음');
    const docId = row.review?.docId;
    if (!docId) return;
    await deleteDoc(doc(db, 'missingClaimReviews', docId));
    console.log('[missingClaims] 처분 되돌림', row.review.legacy ? `legacy:${row.parsedCaseId}` : docId);
}
